export const accountingArticles = [
    {
        id: 'accounting.overview',
        title: 'Accounting Overview',
        category: 'Accounting',
        order: 1,
        summary: 'Financial management and transaction processing',
        content: `
The Accounting page provides comprehensive financial management including invoicing, payments, progress claims, and accounting integrations.

**Tabs:**

- **Dashboard** - Key financial metrics and overview
- **Invoices** - View and manage supplier invoices
- **Invoice Entry** - Enter new invoices
- **PO Matching** - Match invoices to purchase orders
- **Payment Batches** - Process supplier payments
- **Audit Trail** - View all financial transaction history
- **Progress Claim** - Create client claims
- **Budget Progress** - Track budget vs actual
- **Claims History** - View past claims
- **Receipts** - Record client payments
- **Settings** - Accounting configuration, integrations, and ABA setup
    `,
        expandableSections: [
            {
                title: 'Financial Workflow',
                content: `
**Payables Flow:**
1. Receive supplier invoice
2. Enter invoice in system
3. Match to purchase order
4. Create payment batch
5. Generate ABA file
6. Process payment

**Receivables Flow:**
1. Complete work stages
2. Create progress claim
3. Send to client
4. Record receipt when paid
        `,
            },
            {
                title: 'Accounting Integrations',
                content: `
The accounting module can push your records to an accounting platform:

- **Xero** — supplier invoices, payments, claims, receipts, credit notes and journals
- **QuickBooks Online** — the same records

Costed sends documents to the platform when they are approved (or when a person presses Post / Send on the **Posting** tab), and brings back what the bookkeeper does there: payments applied to claims and bills, and — for platform-led workspaces — bills entered against a sent purchase order. Configure the connection in the **Settings** tab within Accounting (owner or admin); the rules live under the main Settings → Accounting → Posting. See the Integrations and Posting help articles for details.

**Who can do what:** a **viewer** sees every screen read-only. An **owner, admin, manager or accounts** role approves invoices, records receipts, runs payment batches, downloads the bank file, posts journals and pushes records to the platform. An **owner or admin** connects or disconnects a platform, edits the GL mappings, changes the company settings (GST rate, approval thresholds) and administers the ledger.
        `,
            },
            {
                title: 'Posting',
                content: `
Every money document moves through the same five states, shown as a chip on its list: **Draft** (entered, not approved) → **Approved** (signed off, waiting for the ledger) → **Posted** (in Costed's ledger: job cost, GST, retention and commitments updated) → **Sent** (in the connected accounting platform, with its reference) → **Settled** (paid or receipted). A document that cannot move on shows **Blocked** with the reason in plain words; one dated before the ledger cut-over shows **History** and is never posted.

**Approve does everything automatically** — post to the ledger, then send to the platform — unless the **Posting rules** page sets a document type to *queued* (the main Settings page, under Accounting → Posting — not the Settings tab inside Accounting, which only holds the connection; that tab has an *Open posting rules* button), in which case Approve stops at Approved and a person presses Post here (the send follows). The same page states the system of record (Costed for everything job-related, the accounting package for bank, BAS, payroll and overheads), whether purchase orders are sent to the platform, whether supplier bills are Costed-led or platform-led, and the period lock date: nothing dated on or before it posts, so a lodged BAS stays as lodged. The **Posting** tab (Accounting → Posting) is the safety net: three queues with counts, **To post**, **To send** and **Blocked**, plus **Sent today**. Press **Post all** / **Send all** with a confirmation, or act on one row; a blocked row's **Retry** repeats the failed step after you fix the cause, and the reason links to the fix (GL Mapping, Integrations, the job). Sending from here ignores the auto-sync switches — it is a person's explicit choice.

Owner, admin, manager and **accounts** roles can post and send; everyone else sees the queues read-only. Supplier invoices, credit notes, progress claims, client receipts and retention releases all follow the lifecycle; payment runs post through their batch and journals are posted from the ledger.

**Overheads with no job.** A supplier invoice entered with no job is a company overhead: it posts to the invoice's own GL account if one was chosen, otherwise to the **Overheads (no job)** posting role (built-in code 6100 — map it under Ledger → GL map and on the platform's GL Mapping), never to work in progress.

**Retention releases** post as a transfer from retention payable to supplier invoices (or retention receivable to progress claims on the client side) and are sent to the platform as a bill or invoice coded to the retention account, with no tax — the GST was on the original document.

**Purchase orders and platform-led bills.** With *Purchase orders: sent to the platform* on the Posting rules page (main Settings → Accounting → Posting) and the *Purchase Orders* sync type ticked on the connection, placing an order also raises it in Xero / QuickBooks (ex-GST lines on the job-cost account, the Costed order number as the reference); orders then appear on the Posting screen with their own Post (commitment) and Send. With *Supplier bills: platform-led*, the bookkeeper enters the bill in the platform against that purchase order — copy the order to a bill in Xero, or "Create bill" from the PO in QuickBooks — and Costed brings it in every night or on **Pull bills from …**: a supplier invoice on the order's job and cost centre, approved and posted, marked as already in the platform. Bills that mention no Costed order are left alone.

**Unapprove after posting.** Unapproving a posted document writes a reversing journal dated today and voids or deletes its copy in the platform when that copy is still unpaid; if the platform copy already has a payment applied, the unapprove is refused and you are told to reverse the payment there first. A re-approval posts and sends afresh.

**Money coming back.** When a client pays and the bookkeeper receipts it in Xero or QuickBooks, or a supplier bill is paid there outside a Costed payment run, Costed brings the payment back: every night, and whenever someone presses **Pull payments from Xero / QuickBooks** on the Posting tab. For each claim or supplier invoice Costed has sent and not yet settled, the platform is asked which payments it has applied; each one is recorded once — a client receipt allocated to the claim, or a supplier payment that closes the invoice — with a note saying it came from the platform, and it is never pushed back. The platform wins for cash, Costed wins for cost: Costed never changes a platform bank transaction..
        `,
            },
        ],
        keywords: ['accounting', 'finance', 'invoices', 'payments', 'claims', 'xero', 'myob', 'quickbooks', 'integrations'],
        route: '/accounting',
        relatedArticles: ['accounting.invoices', 'accounting.payments', 'accounting.claims', 'accounting.integrations'],
        screenshots: [
            {
                src: 'accounting/accounting-invoices.png',
                alt: 'Accounting Invoices tab',
                caption: 'The Invoices tab shows all supplier invoices and their status',
            },
        ],
    },
    {
        id: 'accounting.invoices',
        title: 'Viewing Invoices',
        // approval rules are described in the grid section below
        category: 'Accounting',
        order: 2,
        summary: 'Managing and viewing supplier invoices',
        content: `
The Invoices tab shows all supplier invoices with their matching and payment status.

**Invoice Statuses:**

- **Unmatched** - Not matched to an order
- **Matched** - Matched to order, awaiting payment
- **Part Paid** - Partially paid
- **Paid** - Fully paid
    `,
        expandableSections: [
            {
                title: 'Invoice Grid',
                content: `
**Approval and what may change:** an owner, admin or manager approves an invoice; the approver (or the API key) is recorded on it. Once approved, only its notes, memo, reference and due dates can change — the amount, date, supplier, job and cost centre cannot, and the type never can; unapprove it first. An invoice in a payment batch cannot be unapproved, written off or deleted until it is removed from the batch; a paid invoice can only have its notes changed.

The grid displays:

- **Invoice Number** - Supplier's reference
- **Supplier** - Who sent the invoice
- **Job** - Associated job
- **Order** - Matched order (if any)
- **Amount** - Invoice total
- **Due Date** - Payment due date
- **Status** - Current status

Filter by status, supplier, job, or date range.
        `,
            },
        ],
        keywords: ['invoices', 'bills', 'supplier invoices', 'payables'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.matching'],
    },
    {
        id: 'accounting.entry',
        title: 'Invoice Entry',
        category: 'Accounting',
        order: 3,
        summary: 'How to enter supplier invoices',
        content: `
Enter supplier invoices to track what you owe and prepare for payment.

**Required Information:**

- Supplier
- Invoice Number
- Invoice Date
- Amount (including GST)
    `,
        expandableSections: [
            {
                title: 'Entering an Invoice',
                content: `
1. Go to **Invoice Entry** tab
2. Select the Supplier
3. Enter invoice details:
   - Invoice Number
   - Invoice Date
   - Due Date
   - Amount
4. Optionally select the Job
5. Upload invoice document (optional)
6. Click **Save**

The invoice appears in the Invoices list as "Unmatched".

**Tax code and GST-free invoices.** Amounts are entered GST-inclusive. The Tax Code starts from the supplier: **GST Free** when the supplier is not registered for GST (the ABN check keeps that up to date) or is marked *Supplies are GST-free by default* (superannuation funds, some insurers, government charges); **GST (10%)** otherwise. Change it on the invoice when a particular bill differs. The code decides everything downstream in one place: Costed's ledger posts no GST Receivable line for a GST-free invoice, and the connected platform receives it as GST-free too — Xero "GST Free Expenses", QuickBooks the full amount with the "GST-free non-capital" code, QuickBooks GST-free code. A normal invoice is split into net and GST.
        `,
            },
            {
                title: 'Document Attachment',
                content: `
Attach the supplier's invoice document:

1. Click **Attach Document** or drag-drop
2. Select the PDF/image
3. Document is linked to the invoice record

View attached documents from the invoice grid.
        `,
            },
        ],
        keywords: ['invoice entry', 'enter invoice', 'add invoice', 'bill entry'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.matching'],
    },
    {
        id: 'accounting.matching',
        title: 'Invoice Matching',
        category: 'Accounting',
        order: 4,
        summary: 'Matching invoices to purchase orders',
        content: `
Invoice matching verifies that invoices correspond to purchase orders, ensuring you only pay for what was ordered.

**3-Way Matching:**

1. Purchase Order (what was ordered)
2. Delivery/Receipt (what was received)
3. Invoice (what was billed)
    `,
        expandableSections: [
            {
                title: 'Matching Process',
                content: `
1. Go to **Invoice Matching** tab
2. Select an unmatched invoice
3. System shows potential order matches
4. Select the matching order
5. Compare:
   - Ordered quantities vs invoiced
   - Ordered prices vs invoiced
6. Resolve any discrepancies
7. Confirm the match

Matched invoices move to "Matched" status.
        `,
            },
            {
                title: 'Handling Discrepancies',
                content: `
**Quantity Variances:**
- Short delivery - partial match
- Over delivery - requires approval

**Price Variances:**
- Within tolerance - auto-approve
- Outside tolerance - requires review

**No Matching Order:**
- Manual approval required
- Mark as "approved without order"
        `,
            },
        ],
        keywords: ['matching', 'invoice matching', '3-way match', 'verify'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.invoices'],
    },
    {
        id: 'accounting.payments',
        title: 'Payment Batches',
        category: 'Accounting',
        order: 5,
        summary: 'Processing supplier payments and generating ABA files',
        content: `
Create payment batches to pay multiple suppliers at once and generate ABA files for direct deposit.

**Payment Workflow:**

1. Select invoices to pay
2. Create payment batch
3. Review and approve
4. Generate ABA file
5. Upload to bank
6. Mark as paid
    `,
        expandableSections: [
            {
                title: 'Creating a Payment Batch',
                content: `
1. Go to **Payment Batches** tab
2. Click **New Batch**
3. Set batch details:
   - Payment Date
   - Description
4. Select invoices to include:
   - Filter by due date
   - Select specific invoices
5. Review total amount
6. Click **Create Batch**
        `,
            },
            {
                title: 'Generating ABA File',
                content: `
1. Open the payment batch
2. Verify supplier banking details
3. Click **Generate ABA**
4. Download the ABA file
5. Upload to your bank portal

**ABA Settings:**
Configure your bank details in Settings > Accounting > ABA Configuration.
        `,
            },
            {
                title: 'Completing Payment',
                content: `
After uploading the file to the bank:

1. Return to the batch
2. Click **Process** (Mark processed)
3. Costed records one payment transaction per invoice, marks every invoice in the batch **paid** and sets the batch to Processed — in one step, all or nothing

A paid invoice leaves the payables list, the aged payables report and the supplier statement's outstanding balance, and the assistant reports it as paid. A paid invoice can no longer be edited (except its notes), unapproved, written off or deleted.

**Authorising the batch** names any supplier whose bank details have not been verified (Suppliers → Onboarding); the run still goes ahead — check them before you upload. Only an owner, admin or manager can download the bank file, and the remitter details always come from Settings → Accounting → ABA Configuration.
        `,
            },
        ],
        keywords: ['payments', 'pay', 'ABA', 'direct deposit', 'bank', 'batch'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.invoices', 'settings.aba'],
        screenshots: [
            {
                src: 'accounting/accounting-payment-batches.png',
                alt: 'Payment Batches',
                caption: 'Create payment batches and generate ABA files for direct deposit',
            },
        ],
    },
    {
        id: 'accounting.claims',
        title: 'Progress Claims',
        category: 'Accounting',
        order: 6,
        summary: 'Creating and managing progress claims',
        content: `
Progress claims are how you bill the client for work done on a job. Every claim, however it was raised, becomes one **claim transaction** on the job and shows in the same places: the job's Contract Claims tab, Accounting → Claims, Outstanding Claims, the client portal and the money reports.

**Three ways to raise a claim:**

- **Stage claims** — the job's claim schedule (Deposit, Base, Frame…) from a claim template; post a stage when it is reached
- **Progress (percent-complete) claims** — per cost centre, claim the difference between the last percentage and the new one against the BOQ value
- **Adhoc claims** — a cost centre and an amount

**What posting does (since September 2026):**

- The claim is **authorised the moment it is posted** — issuing it is the approval. There is no separate approve step.
- It is posted to the **ledger** straight away when your ledger is switched on (Dr Trade Debtors, Cr Revenue ex-tax, Cr tax collected). If the ledger post fails — most often because your chart of accounts does not have the account the posting needs — the claim is still saved, the transaction carries the error in its audit-trail metadata, and every owner and admin gets an in-app notification (once a day per problem) so it can be retried from Accounting → Ledger.
- The amount is stored **tax-inclusive** and rounded to the cent; the ex-tax figure and the tax are derived from your workspace's rate, so the job's summary, Accounting → Claims and the reports always agree.
- A stage can only be posted once, and only after it is marked **Ready**. Two people posting the same stage at once cannot both succeed.

**Who can do this:** creating, posting, unposting, authorising and receipting money need the **owner, admin or manager** role. Everyone else can view.
    `,
        expandableSections: [
            {
                title: 'Creating a Claim',
                content: `
**Stage claim** (Jobs → job → Contract Claims):

1. Apply a **claim template** to the job if the schedule is empty (stages must add up to 100 %)
2. Tick **Ready** on the stage that has been reached
3. Click **Post** and confirm — the amount is the stage percentage of the *amended* contract (contract plus authorised variations), tax-inclusive
4. The stage turns **Posted**, and the claim appears in Outstanding Claims until receipts cover it

**Progress or adhoc claim** (Accounting → Claims → New Claim):

1. Pick the job
2. Either enter the **new percent complete** per cost centre, or enter an **amount** per cost centre (adhoc)
3. Review the preview — ex-tax, tax and total are shown at your workspace rate
4. Click **Create** — one transaction per cost centre is created, authorised, and listed in Outstanding Claims

**Unposting** a stage deletes its transaction and returns the stage to Ready. A stage that already has a receipt allocated to it cannot be unposted — record a reversing receipt first.
        `,
            },
            {
                title: 'Claim Document',
                content: `
Labels on every claim screen, report and client document use your workspace's **tax name** — "inc GST", "inc VAT" or "inc Tax" — set in Settings → Accounting alongside the rate. Amounts are always shown both ex-tax and tax-inclusive so there is no ambiguity about which figure the client owes.

**Tax invoice PDF.** Every posted claim has a tax invoice numbered **CLM-{job}-{stage}** (the same number the accounting sync sends to Xero/QuickBooks): the stage claimed, amount ex-tax / tax / inclusive, the contract position to date (contract, variations, amended contract, previously claimed, this claim, received, balance outstanding), the due date from your default payment terms, and how to pay from your ABA bank details. Open it from the **Invoice** column of the Claims list on the job's Contract Claims tab.

**Send to client.** The envelope button emails the invoice (PDF attached) to everyone with portal access on the job, once — sending again asks you to confirm a resend. Clients can also download any claimed stage's invoice from the **Payment stages** list in their portal.
        `,
            },
        ],
        keywords: ['claims', 'progress claims', 'billing', 'invoice client'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.receipts', 'jobs.claims'],
        screenshots: [
            {
                src: 'accounting/accounting-progress-claims.png',
                alt: 'Progress Claims',
                caption: 'Create and manage progress claims for client billing',
            },
        ],
    },
    {
        id: 'accounting.receipts',
        title: 'Recording Receipts',
        category: 'Accounting',
        order: 7,
        summary: 'Recording client payments against claims',
        content: `
Record what the client paid against a job. Receipts are **tax-inclusive** (the amount that landed in the bank) and are what turn a posted claim into a **paid** one.

**A receipt records:**

- Date and amount received (must be greater than zero)
- Reference (remittance / bank reference)
- Bank account
- Optionally the **stage** it settles — only a stage that has been posted can be chosen; the server rejects anything else
    `,
        expandableSections: [
            {
                title: 'Recording a Receipt',
                content: `
1. Open the job → **Contract Claims** tab → **Record Receipt** (or Accounting → Receipts)
2. Enter the date, the amount received and the reference
3. Choose the **stage** the money is for (leave blank for an unallocated receipt)
4. Click **Save**

When the receipts allocated to a stage add up to what was claimed for it, the stage becomes **Paid** with the receipt's date as its paid date, and it drops off Outstanding Claims.
        `,
            },
            {
                title: 'Partial Payments',
                content: `
Enter the actual amount received and allocate it to the stage. The stage stays **Posted** and Outstanding Claims shows the remaining **balance** (claimed minus receipts). Record further receipts against the same stage until it is covered — the last one marks it Paid.
        `,
            },
        ],
        keywords: ['receipts', 'payments received', 'client payments', 'receivables'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.claims'],
    },
    {
        id: 'accounting.budget',
        title: 'Budget Progress',
        category: 'Accounting',
        order: 8,
        summary: 'Tracking budget vs actual expenditure',
        content: `
The Budget Progress tab shows how actual costs compare to budgeted amounts by cost centre.

**Key Metrics:**

- Budget (from BOQ)
- Committed (orders placed)
- Actual (invoiced/paid)
- Variance
    `,
        expandableSections: [
            {
                title: 'Reading the Report',
                content: `
Each cost centre shows:

| Column | Description |
|--------|-------------|
| Budget | BOQ total for the cost centre |
| Committed | Value of placed orders |
| Actual | Invoiced amounts |
| Variance | Budget - Actual |

**Status Indicators:**
- Green: Under budget
- Yellow: Within tolerance
- Red: Over budget
        `,
            },
        ],
        keywords: ['budget', 'actual', 'variance', 'cost tracking', 'expenditure'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'boq.cost-centres'],
    },
    {
        id: 'accounting.integrations',
        title: 'Accounting Integrations',
        category: 'Accounting',
        order: 9,
        summary: 'Connect to Xero or QuickBooks for accounting sync',
        content: `
The Integrations panel lets you connect Costed.app to your accounting platform for automatic data synchronisation.

**Supported Platforms:**

- **Xero** — Australian-popular cloud accounting
- **QuickBooks Online** — Intuit's cloud accounting (Australian companies, GST codes handled)

Once connected, approved documents flow to the platform automatically and payments flow back. Costed is the system of record for job cost; the platform stays the system of record for the bank, BAS and payroll.
    `,
        expandableSections: [
            {
                title: 'Connecting a Platform',
                content: `
1. Go to **Accounting** > **Settings** tab
2. Click **Integrations**
3. Click **Connect** next to your platform (Xero or QuickBooks)
4. You'll be redirected to the platform's login page
5. Authorise Costed.app to access your account
6. You're returned to Costed.app with the connection active

Connecting and disconnecting is an owner or admin action. The connection link is signed and expires after ten minutes; if the platform brings you back with "expired", start again from Settings. The platform's own credentials (client id, secret and redirect URI) must be set on the Costed server first — until they are, the Connect button says "not configured".

**Test connection** — after connecting, click **Test** to make a live round trip: it reports the organisation the platform sees and how many accounts it returned, or the error. "Connected" on its own only means a token is stored.
        `,
            },
            {
                title: 'GL Mapping',
                content: `
GL (General Ledger) mapping links your local account codes to the corresponding accounts in your connected platform.

**Accounts Costed posts to** — the top of the GL Mapping tab lists exactly what needs a mapping: the fourteen posting roles (cash at bank, progress claims receivable, supplier invoices, GST collected, progress claim revenue, the default job cost, …) with a plain-English "used for", followed by any other account that carries postings on your journals or coded transactions. Each row shows the platform account it is mapped to today, or a **suggestion** matched from the platform's chart: same code or same name is marked *Suggested*; a role keyword found in an account of the right type is marked *Suggested — please confirm*. Rows with no close match are highlighted and say what to create; **Create in Xero / QuickBooks** on the row creates that account in the platform with the right type and maps it in one step (bank accounts are the exception: the platform needs a bank account number, so add those there and press Refresh). Or pick an existing account from the list.

1. Go to **Accounting** > **Settings** > **Integrations** > **GL Mapping** (owner or admin to change anything).
2. Press **Apply N suggestions** to save every unmapped row that has one in a single step (a confirmation lists them), or choose an account on a row and press its tick.
3. **All mappings** below holds every saved mapping, including codes outside the table; add, edit or switch sync off per mapping there.

One mapping per role is enough: the syncs ask by the built-in code (1100, 4110, 5100 …) and Costed also tries the account that code resolves to under **Ledger → GL map**, so a migrated chart (1100 → 1110) needs no duplicate rows.

**Import chart from Xero / QuickBooks** (owner or admin) — for a business that already runs its accounts on the platform and is new to Costed. The button previews first: every active platform account that is missing in Costed is created with the same code, name and type (bank and current assets become assets, cost of sales becomes an expense); accounts that already exist are left untouched; each platform account is mapped to itself for the sync; and any posting role that still resolves to nothing is pointed at the best match (cash at bank → the bank account, supplier invoices → accounts payable, …). Archived accounts, accounts without a code and header accounts are skipped and listed. Nothing is deleted or renamed, and running it again changes nothing. Review **Ledger → GL map** afterwards and add the two retention accounts on the platform if it has none.

Mappings tell the platform which of its accounts each of your GL accounts lands in. An unmapped account is sent without an account reference, and whether the platform accepts that depends on the platform — map everything you post to.

**Ledger posting codes (Costed's own ledger)** — postings inside Costed name accounts by built-in codes (1100 cash at bank, 2110 supplier invoices, 4110 progress claim revenue, …). If your chart uses different codes, map each built-in code to your account under **Ledger → GL map** (owner or admin); the ledger status lists any built-in code that resolves to nothing, and a posting to one of those fails and is flagged on the transaction.
        `,
            },
            {
                title: 'Auto-Sync Configuration',
                content: `
Choose the platform, turn auto-sync on and pick which record types it pushes. When auto-sync is on, a record is pushed right after it posts to Costed's ledger (an approved invoice, a processed payment batch, a claim, a receipt); when it is off, use **Sync** on the record or **Sync all pending** (an owner, admin or manager; at most 200 records per run).

| Sync Type | Description |
|-----------|-------------|
| **Invoices** | Supplier invoices |
| **Payments** | Payment batches |
| **Claims** | Progress claims |
| **Receipts** | Client payment receipts |
| **Batch Payments** | ABA batch payment records |
| **Journal Entries** | Manual journal adjustments |
| **Credit Notes** | Credit note transactions |

Enable or disable each type independently. Records are pushed from Costed to the platform; a change made in the platform is not read back into Costed.
        `,
            },
            {
                title: 'Sync History',
                content: `
View the history of all sync operations:

- **Timestamp** — When the sync occurred
- **Type** — What was synced (invoice, payment, etc.)
- **Status** — Synced, Pending or Failed
- **Details** — Error messages for failed syncs; **Retry failed** (owner or admin) queues them again

Use sync history to troubleshoot issues or verify that data is flowing correctly.
        `,
            },
        ],
        keywords: ['integrations', 'xero', 'quickbooks', 'myob', 'sync', 'gl mapping', 'accounting integration'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'settings.accounting'],
    },
    {
        id: 'accounting.audit-trail',
        title: 'Audit Trail',
        category: 'Accounting',
        order: 10,
        summary: 'View history of all financial transactions and changes',
        content: `
The Audit Trail tab provides a complete history of all financial transactions and changes within the accounting module.

**What's Tracked:**

- Invoice creation, matching, and payment
- Payment batch creation and processing
- Progress claim creation and approval
- Receipt recording
- Any modifications to financial records

**Using the Audit Trail:**

- Search by date range, user, or transaction type
- Filter by specific jobs or suppliers
- View the full detail of each transaction
- Export audit data for compliance and reporting
    `,
        keywords: ['audit', 'audit trail', 'history', 'transactions', 'compliance', 'log'],
        route: '/accounting',
        relatedArticles: ['accounting.overview'],
    },
    {
        id: 'accounting.variations',
        title: 'Variation Orders',
        category: 'Accounting',
        order: 8,
        summary: 'Manage contract variations (change orders) with approval workflow',
        keywords: ['variation', 'change order', 'VO', 'addition', 'omission', 'claim'],
        content: `
Variation Orders track additions and omissions to a building contract. They are managed from the **Jobs → Contract Claims** tab.

**Variation Lifecycle:**

1. **Draft** — Create and add line items
2. **Pending** — Submitted for client approval
3. **Authorised** — Approved, impacts amended contract price
4. **Rejected** — Client declined (can be deleted or revised)

**Two Modes:**

- **Simple** — Quick entry: description + debit (addition) / credit (omission) amounts
- **Detailed** — BOQ-style: cost centre, catalogue item, quantity, unit price

**Key Features:**

- Inline catalogue search in description field
- Cost centre filtering for catalogue search
- Markup % (internal, hidden from client on PDF)
- Expandable notes per line item
- PDF generation with client-facing layout and signature blocks
- OK to Claim flag for including in progress claims
- Authorisation creates a transaction (tran_type=5) for accounting
    `,
        expandableSections: [
            {
                title: 'Creating a Variation',
                content: `
1. Go to **Jobs** → open a job → **Contract Claims** tab
2. Scroll to the **Variations** section
3. Click **+ New Variation**
4. Enter a **Description** and choose **Simple** or **Detailed** mode
5. Click **Create & Edit** to open the full-screen editor
        `,
            },
            {
                title: 'Adding Line Items',
                content: `
**Simple mode:** Enter description, toggle +/- for addition or omission, enter amount, click Add Line.

**Detailed mode:** Select cost centre (searchable), enter description (or type to search catalogue — matching items appear in a dropdown), enter quantity and unit price. The amount is calculated automatically.

**From Catalogue:** Click the Catalogue button to open a search sidebar. Click items to add them as variation lines with their catalogue price.

**CC Filter toggle:** When enabled, catalogue search only shows items from the selected cost centre.
        `,
            },
            {
                title: 'Markup',
                content: `
Set the **Markup %** in the totals section. This is applied to the subtotal and included in the total shown to the client, but the breakdown is **never shown on the PDF**. The client sees only the net totals.
        `,
            },
            {
                title: 'PDF Preview',
                content: `
Click the **PDF** button in the editor header or from the table dropdown. The PDF includes:

- Company header and job details
- Client information
- Line items table (simple or detailed format)
- Additions and omissions totals
- Net variation (Inc GST)
- Signature blocks for builder and client
        `,
            },
            {
                title: 'Approval Workflow',
                content: `
1. **Submit** — moves from Draft to Pending. Line items are locked from here on; only a Draft can be edited.
2. **Authorise** — enter the approver's name. This creates the variation transaction (tax-inclusive, at your workspace rate), authorises it, and posts it to the ledger against **Variation Revenue** when the ledger is on. Two people authorising at once cannot both succeed — the second sees "already authorised".
3. **Reject** — enter the reason.
4. **OK to Claim** — only an authorised variation can be flagged; it is then included when the next stage is posted.
5. **Send to client** — shows it in the client portal and emails the client once; sending again does not re-email.

Authorised variations are included in the **Amended Contract** at the top of the Contract Claims tab, so later stage claims are calculated on contract + variations.

Money actions on variations (create, submit, authorise, reject, OK to claim) need the owner, admin or manager role.
        `,
            },
        ],
        relatedArticles: ['accounting.overview', 'jobs.overview'],
    },
];
